import { cn, getRatioClassnames } from '@/lib/utils/cn';
import React, { ReactNode } from 'react';

type HeroProps = Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> & {
  meta?: ReactNode;
  title: string | ReactNode;
  description: ReactNode;
  cta?: ReactNode;
  variant?: 'default' | 'image';
  image?: string;
  imageAlt?: string;
  footer?: ReactNode;
  ratio?: string;
};

const Hero: React.FC<HeroProps> = ({
  meta,
  ratio = '1:1',
  title,
  description,
  cta,
  variant = 'default',
  image,
  imageAlt,
  className,
  footer,
  ...props
}) => {
  if (variant === 'image' && image) {
    const [leftClass, rightClass] = getRatioClassnames(ratio);

    return (
      <section className={cn('mx-auto max-w-screen-xl dark:bg-gray-900', className)} {...props}>
        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12 items-center px-4 py-8 md:px-2 lg:px-2 lg:py-12 xl:px-0">
          <div className={cn('text-left flex-1', leftClass)}>
            {meta && (
              <div className="inline-block uppercase mb-4 px-2 py-1 bg-primary/10 shadow-sm text-primary-dark rounded-md text-sm font-medium">
                {meta}
              </div>
            )}
            <h1 className="mb-6 text-3xl font-extrabold tracking-tight text-gray-900 dark:text-white md:text-4xl lg:text-5xl">{title}</h1>
            <div className="mb-6 text-lg font-normal text-gray-600 dark:text-gray-400 lg:text-xl">{description}</div>
            {cta && <div className="flex flex-col space-y-4 w-full sm:w-auto sm:flex-row sm:space-x-4 sm:space-y-0">{cta}</div>}
            {footer && <div className="mt-8">{footer}</div>}
          </div>
          <div className={cn('relative flex-1 p-5', rightClass)}>
            <img src={image} alt={imageAlt || 'Hero image'} className="w-full h-auto rounded-2xl" />
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      className={cn(
        'mx-auto flex max-w-screen-xl flex-col items-center justify-center px-4 py-8 text-center dark:bg-gray-900 lg:px-12 lg:py-16',
        className
      )}
      {...props}
    >
      {meta && <div className="block mb-5 uppercase text-2xl">{meta}</div>}
      <h1 className="mb-4 text-3xl font-extrabold leading-none tracking-tight text-gray-900 dark:text-white md:text-4xl lg:text-5xl">{title}</h1>
      <div className="text-lg font-normal text-gray-500 dark:text-gray-400 sm:px-16 lg:text-xl xl:px-48">{description}</div>
      {cta && <div className="mt-8 flex flex-col space-y-4 sm:flex-row sm:justify-center sm:space-x-4 sm:space-y-0 w-full sm:w-auto">{cta}</div>}
    </section>
  );
};

export default Hero;
